import React, { useEffect, useState } from 'react';
import { Layout, Grid } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  getAdminMenuGroupKey,
  getAdminPageTitle,
} from './adminMenu';
import { AdminHeader } from './AdminHeader';
import { AdminSidebar } from './AdminSidebar';
import { AdminBreadcrumb } from './AdminBreadcrumb';
import { AdminContentShell } from './AdminContentShell';
import { useAdminNotifications } from '../../hooks/useAdminNotifications';

const { Sider } = Layout;
const { useBreakpoint } = Grid;

export const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const [collapsed, setCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const { notifications, loading, error, refetch } = useAdminNotifications();

  const effectiveCollapsed = isMobile ? !mobileSidebarOpen : collapsed;

  useEffect(() => {
    const groupKey = getAdminMenuGroupKey(location.pathname);
    if (groupKey) {
      setOpenKeys([groupKey]);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) {
      setMobileSidebarOpen(false);
    }
  }, [isMobile]);

  const handleUserMenuClick = (key: string) => {
    if (key === 'logout') {
      localStorage.removeItem('admin_token');
      navigate('/login');
    } else if (key === 'profile') {
      navigate('/profile');
    } else if (key === 'settings') {
      navigate('/settings');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', display: 'flex', flexDirection: 'row' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={effectiveCollapsed}
        onCollapse={(next) => {
          if (isMobile) {
            setMobileSidebarOpen(!next);
            return;
          }
          setCollapsed(next);
        }}
        width={260}
        collapsedWidth={isMobile ? 0 : 80}
        theme="dark"
        className="bo-admin-sidebar"
        style={{
          background: 'var(--sidebar-bg)',
          boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
          zIndex: isMobile ? 100 : 98,
          position: isMobile ? 'fixed' : 'sticky',
          left: 0,
          top: 0,
          bottom: 0,
          height: '100vh',
        }}
      >
        <AdminSidebar
          collapsed={effectiveCollapsed}
          isMobile={isMobile}
          openKeys={openKeys}
          onOpenKeysChange={setOpenKeys}
          onToggleCollapsed={() => setCollapsed((v) => !v)}
          onNavigate={isMobile ? () => setMobileSidebarOpen(false) : undefined}
        />
      </Sider>

      {isMobile && mobileSidebarOpen && (
        <div
          onClick={() => setMobileSidebarOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.25)',
            zIndex: 99,
          }}
        />
      )}

      <Layout style={{ flex: 1, minWidth: 0 }}>
        <AdminHeader
          isMobile={isMobile}
          title={getAdminPageTitle(location.pathname)}
          onToggleSidebar={isMobile ? () => setMobileSidebarOpen((v) => !v) : undefined}
          onUserAction={handleUserMenuClick}
          notifications={notifications}
          notificationsLoading={loading}
          notificationsError={error?.message ?? null}
          onNotificationsRetry={() => {
            void refetch();
          }}
        />
        <AdminContentShell isMobile={isMobile}>
          <AdminBreadcrumb />
          <div
            style={{
              background: '#fff',
              padding: isMobile ? 12 : 20,
              borderRadius: 10,
              minHeight: 'calc(100vh - 140px)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            <Outlet />
          </div>
        </AdminContentShell>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
